import type { Town, NPC, BuildingData, TownObjectData } from '@/game/types';
import type { InitHeroResponse, RoadSegmentPayload, RoadSettlementPayload } from '@/network/hero.api';
import { apiGet } from '@/network/api-client';
import { ApiError } from '@/network/api-client';
import type { TownLayoutPayload } from '@/modules/game-domain/authored-town.types';

export async function getTownLayout(townId: number): Promise<TownLayoutPayload | null> {
  try { return await apiGet<TownLayoutPayload>(`/towns/${townId}/layout`); }
  catch (error) { if (error instanceof ApiError && error.status === 404) return null; throw error; }
}

export async function getTowns(): Promise<Town[]> {
  return apiGet<Town[]>('/towns');
}

export async function getRoads(): Promise<RoadSegmentPayload[]> {
  return apiGet<RoadSegmentPayload[]>('/roads');
}

export async function getRoadSettlements(): Promise<RoadSettlementPayload[]> {
  return apiGet<RoadSettlementPayload[]>('/road-settlements');
}

export async function getTownNPCs(townId: number): Promise<NPC[]> {
  return apiGet<NPC[]>(`/towns/${townId}/npcs`);
}

export async function getTownBuildings(townId: number): Promise<BuildingData[]> {
  return apiGet<BuildingData[]>(`/towns/${townId}/buildings`);
}

export async function getTownObjects(townId: number): Promise<TownObjectData[]> {
  return apiGet<TownObjectData[]>(`/towns/${townId}/objects`);
}

export function defaultNpcShopCosts(): {
  potionCost: number;
  healCost: number;
  potionBase: number;
  healBase: number;
  potionPerLevel: number;
  healPerLevel: number;
} {
  return {
    potionCost: 44,
    healCost: 92,
    potionBase: 40,
    healBase: 80,
    potionPerLevel: 4,
    healPerLevel: 12,
  };
}

export function effectiveNpcShopCosts(
  bundle: NpcShopBundle,
  level: number,
): { potionCost: number; healCost: number } {
  const lv = level > 0 ? level : 1;
  return {
    potionCost: bundle.potionBase + bundle.potionPerLevel * lv,
    healCost: bundle.healBase + bundle.healPerLevel * lv,
  };
}

export function defaultMerchantGearCosts(): {
  merchantGearCostBase: number;
  merchantGearCostPerTownLevel: number;
} {
  return { merchantGearCostBase: 35, merchantGearCostPerTownLevel: 6 };
}

export type NpcShopBundle = ReturnType<typeof defaultNpcShopBundle>;

export function defaultNpcShopBundle() {
  return { ...defaultNpcShopCosts(), ...defaultMerchantGearCosts() };
}

export function npcShopCostsFromInit(res: InitHeroResponse): NpcShopBundle {
  const d = defaultNpcShopBundle();
  const p = res.npcCostPotion;
  const h = res.npcCostHeal;
  const pb = res.npcCostPotionBase;
  const hb = res.npcCostHealBase;
  const pp = res.npcCostPotionPerLevel;
  const hp = res.npcCostHealPerLevel;
  const gb = res.merchantTownGearCostBase;
  const gp = res.merchantTownGearCostPerTownLevel;
  const potionBase = typeof pb === 'number' && pb > 0 ? pb : d.potionBase;
  const healBase = typeof hb === 'number' && hb > 0 ? hb : d.healBase;
  const potionPerLevel = typeof pp === 'number' && pp >= 0 ? pp : d.potionPerLevel;
  const healPerLevel = typeof hp === 'number' && hp >= 0 ? hp : d.healPerLevel;
  return {
    potionCost: typeof p === 'number' && p > 0 ? p : d.potionCost,
    healCost: typeof h === 'number' && h > 0 ? h : d.healCost,
    potionBase,
    healBase,
    potionPerLevel,
    healPerLevel,
    merchantGearCostBase: typeof gb === 'number' && gb > 0 ? gb : d.merchantGearCostBase,
    merchantGearCostPerTownLevel:
      typeof gp === 'number' && gp >= 0 ? gp : d.merchantGearCostPerTownLevel,
  };
}

export type { RoadSegmentPayload, RoadSettlementPayload, InitTownPayload } from '@/network/hero.api';
